import {
	getItemFromLocalStorage,
	removeItemFromLocalStorage,
	setItemFromLocalStorage,
} from '../utils/localstorage';

export const AUTH_TOKEN_KEY = 'grizzly_tg_authToken';
export const REFRESH_TOKEN_KEY = 'grizzly_tg_refreshToken';
export const ACCESS_EXPIRES_AT_KEY = 'grizzly_tg_accessTokenExpiresAt';

const REFRESH_SKEW_SEC = 45;
const DEFAULT_ACCESS_TTL_SEC = 3600;
const DEFAULT_REFRESH_TTL_SEC = 2592000;

/**
 * Prefer access_token; fall back to token (legacy tg-user-auth / refresh alias).
 */
export function resolveAccessToken(payload = {}) {
	if (payload.access_token) return payload.access_token;
	if (payload.token) return payload.token;
	return null;
}

export function normalizeAuthPayload(payload = {}) {
	const accessToken = resolveAccessToken(payload);
	const refreshToken = payload.refresh_token || null;
	const nowSec = Math.floor(Date.now() / 1000);

	const accessExpiresAt =
		Number(payload.access_token_expires_at) ||
		(payload.expires_in != null ? nowSec + Number(payload.expires_in) : null) ||
		(accessToken ? nowSec + DEFAULT_ACCESS_TTL_SEC : null);

	const refreshExpiresAt =
		Number(payload.refresh_token_expires_at) ||
		(payload.refresh_expires_in != null
			? nowSec + Number(payload.refresh_expires_in)
			: null) ||
		(refreshToken ? nowSec + DEFAULT_REFRESH_TTL_SEC : null);

	return {
		accessToken,
		refreshToken,
		accessExpiresAt,
		refreshExpiresAt,
		tokenType: payload.token_type || 'Bearer',
	};
}

export function isAccessTokenExpiringSoon(expiresAtUnix, skewSec = REFRESH_SKEW_SEC) {
	if (!expiresAtUnix) return false;
	return Number(expiresAtUnix) - skewSec <= Math.floor(Date.now() / 1000);
}

export function readAuthSession() {
	const accessExpiresRaw = getItemFromLocalStorage(ACCESS_EXPIRES_AT_KEY);
	return {
		accessToken: getItemFromLocalStorage(AUTH_TOKEN_KEY) || null,
		refreshToken: getItemFromLocalStorage(REFRESH_TOKEN_KEY) || null,
		accessExpiresAt: accessExpiresRaw ? Number(accessExpiresRaw) : null,
	};
}

function applyBearerHeader(agent, accessToken) {
	if (!agent?.defaults) return;
	agent.defaults.headers = agent.defaults.headers || {};
	if (accessToken) {
		agent.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
	} else {
		delete agent.defaults.headers['Authorization'];
	}
}

/**
 * Persist access/refresh from login / refresh / migrate response into localStorage + axios.
 */
export function persistAuthSession({ agent, payload } = {}) {
	const normalized = normalizeAuthPayload(payload);
	if (!normalized.accessToken) {
		return null;
	}

	setItemFromLocalStorage(AUTH_TOKEN_KEY, normalized.accessToken);

	if (normalized.refreshToken) {
		setItemFromLocalStorage(REFRESH_TOKEN_KEY, normalized.refreshToken);
	}

	if (normalized.accessExpiresAt) {
		setItemFromLocalStorage(ACCESS_EXPIRES_AT_KEY, String(normalized.accessExpiresAt));
	}

	applyBearerHeader(agent, normalized.accessToken);
	if (agent) {
		agent.accessExpiresAt = normalized.accessExpiresAt;
	}

	return normalized;
}

export function clearAuthSession({ agent } = {}) {
	removeItemFromLocalStorage(AUTH_TOKEN_KEY);
	removeItemFromLocalStorage(REFRESH_TOKEN_KEY);
	removeItemFromLocalStorage(ACCESS_EXPIRES_AT_KEY);
	applyBearerHeader(agent, null);
	if (agent) {
		agent.accessExpiresAt = null;
	}
}

/**
 * Restore Authorization header from localStorage (e.g. after reload before re-auth).
 */
export function hydrateAuthFromStorage(agent) {
	const session = readAuthSession();
	if (session.accessToken) {
		applyBearerHeader(agent, session.accessToken);
		if (agent) {
			agent.accessExpiresAt = session.accessExpiresAt;
		}
	}
	return session;
}
